'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useUnit } from 'effector-react';
import { loadCss, loadJs } from '@nx-frontend/features/web-editor/util';
import { $ModalStore, closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal/modal-store';
import { SkeletonType, SkeletonWrapper } from '@nx-frontend/shared/ui/skeletons';
import { generalEnv } from '@nx-frontend/shared/env';
import { useRouter } from '@nx-frontend/shared/hooks/router-proxy';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import {
  EditorColorScheme,
  ManagePopupPayload,
  PaywallPopupPayload,
  PaywallPopupReason,
  PopupType,
  TranslatesFnType,
} from '@nx-frontend/shared/types';
import { GetOpenManagePopupProps } from '@nx-frontend/shared/types';
import { NextRouterType } from '@nx-frontend/shared/utils';
import { getOpenInvitePopup } from './invite-popup';
import { getOpenPaywallPopup } from './paywall-popup';

const webComponentTag = 'nim-manage';
const isManagePopupBundleLoaded = () => document.createElement(webComponentTag).constructor !== HTMLElement;
let isApiInited = false;

export const getOpenManagePopup =
  (t: TranslatesFnType) =>
  ({ isFreePlan, isOwner, openModal }: GetOpenManagePopupProps) =>
  (payload: ManagePopupPayload): void => {
    if (isFreePlan) {
      if (isOwner) {
        const paywallPopupPayload: PaywallPopupPayload = {
          payloadType: PopupType.PAYWALL,
          title: t('features.paywall.title_keep_your_data_under_control'),
          reason: PaywallPopupReason.MANAGE_MEMBERS_GRANULAR,
        };

        getOpenPaywallPopup({ openModal, payload: paywallPopupPayload });
      } else {
        openModal({
          title: t('webnotes.webnotes-granular_perm_not_available'),
          content: PopupType.DIALOG,
          closeButtonSize: 18,
          multipopup: false,
          payload: {
            payloadType: PopupType.DIALOG,
            text: t('webnotes.webnotes-granular_perm_not_available_description'),
            buttons: [
              {
                text: t('webnotes.webnotes-ok'),
                main: true,
              },
            ],
          },
        });
      }

      return;
    }

    openModal({
      content: PopupType.MANAGE,
      showCloseButton: false,
      payload,
    });
  };

export function ManagePopup() {
  const [app, currentWorkspace, billingInfo, orgInfo, role, modalStore, canManageMembers] = useUnit([
    $app,
    $currentWorkspace,
    $billingInfo,
    $orgInfo,
    $orgRoleState,
    $ModalStore,
    $wpCanManageMembers,
  ]);
  const [closeModal, openModal] = useUnit([closeModalEvent, openModalEvent]);
  const [isLoaded, setIsLoaded] = useState(false);
  const nimInviteRef = useRef<HTMLDivElement>(null);

  const router = useRouter();
  const routerType = NextRouterType.APP;
  const routerPushFn = router.push;
  const modal = modalStore.find((modal) => modal.payload && modal.payload.payloadType === PopupType.MANAGE);
  const payload = modal?.payload as ManagePopupPayload;

  const onManagePopupLoaded = useCallback(() => {
    if (!isApiInited) {
      isApiInited = true;
      const setupApiEvent = new CustomEvent('ng2:management:setup', {
        detail: {
          gatewayUrl: generalEnv.gatewayUrl,
          gatewayToken: app.sessionId,
          staticAssetsPrefix: generalEnv.staticAssetPrefix,
        },
      });
      document.dispatchEvent(setupApiEvent);

      const loadAllEvent = new CustomEvent('ng2:management:load-all', {
        detail: {
          organizationId: currentWorkspace.orgId,
        },
      });
      document.dispatchEvent(loadAllEvent);
    }

    const nimManage = document.createElement(webComponentTag);
    Object.assign(nimManage, {
      featureContext: {
        organizationId: currentWorkspace.orgId,
        workspaceId: currentWorkspace.globalId,
      },
      featureSubject: {
        ...payload,
        isDefaultOrInside:
          (payload?.subjectType === 'folder' && payload.globalId === 'default') ||
          (payload?.subjectType === 'note' && payload.parentId === 'default'),
      },
    });

    nimManage.addEventListener('nimClose', () => {
      closeModal();
    });

    nimManage.addEventListener('nimAddMembers', () => {
      if (payload) {
        getOpenInvitePopup(
          {
            orgInfo,
            billingInfo,
            currentWorkspace,
            role,
            canManageMembers,
          },
          openModal,
          { routerType, routerPushFn }
        )({
          payloadType: PopupType.INVITE,
          subjectType: payload.subjectType,
          globalId: payload.globalId,
          parentId: payload.parentId,
          title: payload.title,
          backToManage: payload,
          skipInviteRedirect: true,
        });
      }
    });

    setIsLoaded(true);
    nimInviteRef.current?.appendChild(nimManage);
  }, [
    currentWorkspace,
    payload,
    app.sessionId,
    closeModal,
    orgInfo,
    billingInfo,
    role,
    canManageMembers,
    openModal,
    routerType,
    routerPushFn,
  ]);

  useEffect(() => {
    loadCss({ url: generalEnv.tasksBundleCssUrl, id: 'tasks-bundle-css' });
    loadJs({ url: generalEnv.invitesBundleJsUrl, id: 'invite-popup-bundle' });

    let IID: NodeJS.Timer | number | null = null;
    if (!isManagePopupBundleLoaded()) {
      IID = setInterval(() => {
        if (isManagePopupBundleLoaded()) {
          onManagePopupLoaded();
          IID && clearInterval(IID);
        }
      }, 1000);
    } else {
      onManagePopupLoaded();
    }

    return () => {
      IID && clearInterval(IID);
      document.querySelector(webComponentTag)?.remove();
    };
  }, [onManagePopupLoaded]);

  return (
    <div
      data-cy="manage-members-popup"
      className="w-[602px] min-h-[200px] relative nimbus-popup-content"
      ref={nimInviteRef}
    >
      {!isLoaded && <SkeletonWrapper type={SkeletonType.INVITE_POPUP} editorColorScheme={EditorColorScheme.LIGHT} />}
    </div>
  );
}
